import { DocumentStatus } from '@/components/StatusTag'
import type { DocumentRecord, DocumentStats } from '@/types/catalog'
import { getDocumentStatus } from './documentStatus'

export interface CatalogStats {
  totalDocuments: number
  totalSizeBytes: number
  processedCount: number
  processingCount: number
  failedCount: number
}

/** Which banner stat bucket a document falls into. */
export type StatBucket = 'processed' | 'processing' | 'failed'

const BUCKET_COUNT_KEY: Record<StatBucket, 'processed_count' | 'processing_count' | 'failed_count'> = {
  processed: 'processed_count',
  processing: 'processing_count',
  failed: 'failed_count',
}

/**
 * Resolve a document to a banner stat bucket using the SAME status rules as the
 * table (`getDocumentStatus`), so the header count never disagrees with the
 * row's status pill. Anything that isn't terminally failed/succeeded (pending,
 * uploading, processing, and every intermediate `*_started`/`*_completed`
 * status) counts as "processing".
 */
export function statBucketForDocument(document: DocumentRecord): StatBucket {
  const displayStatus = getDocumentStatus(document)
  if (displayStatus === DocumentStatus.failed) return 'failed'
  if (displayStatus === DocumentStatus.success) return 'processed'
  return 'processing'
}

/** Aggregate stats for the data catalog banner — uses the same status rules as the table (`getDocumentStatus`). */
export function computeCatalogStats(
  documents: DocumentRecord[],
  totalDocuments: number
): CatalogStats {
  let totalSizeBytes = 0
  let processedCount = 0
  let processingCount = 0
  let failedCount = 0
  for (const doc of documents) {
    totalSizeBytes += doc.file_size ?? 0
    switch (statBucketForDocument(doc)) {
      case 'failed':
        failedCount++
        break
      case 'processed':
        processedCount++
        break
      default:
        processingCount++
    }
  }
  return { totalDocuments, totalSizeBytes, processedCount, processingCount, failedCount }
}

/**
 * FS-603: keep the banner aggregate (`DocumentStats`, computed by the backend
 * over ALL docs) in step with a local optimistic insert/removal of a single
 * row, so the header count doesn't lag the table until the next full refetch.
 * Returns a new stats object with the row's bucket count and total size moved
 * by `delta` (counts clamped at 0). Passthrough when `stats` is null (backend
 * returned no aggregate). Self-heals: the next `fetchDocuments()` overwrites
 * `documentStats` with the authoritative backend aggregate.
 */
export function adjustStatsForDocument(
  stats: DocumentStats | null,
  document: DocumentRecord,
  delta: 1 | -1
): DocumentStats | null {
  if (!stats) return stats
  const key = BUCKET_COUNT_KEY[statBucketForDocument(document)]
  const size = document.file_size ?? 0
  const next: DocumentStats = { ...stats }
  next[key] = Math.max(0, next[key] + delta)
  next.total_size_bytes = Math.max(0, next.total_size_bytes + delta * size)
  return next
}

/**
 * FS-603: move a single row between banner buckets when its live status changes
 * (e.g. an SSE `processing` → `success` event), so the header count tracks the
 * row's status pill immediately instead of only on the next refetch. A no-op
 * when the bucket is unchanged or `stats` is null.
 */
export function applyStatusTransitionToStats(
  stats: DocumentStats | null,
  oldDocument: DocumentRecord,
  newDocument: DocumentRecord
): DocumentStats | null {
  if (!stats) return stats
  const fromKey = BUCKET_COUNT_KEY[statBucketForDocument(oldDocument)]
  const toKey = BUCKET_COUNT_KEY[statBucketForDocument(newDocument)]
  if (fromKey === toKey) return stats
  const next: DocumentStats = { ...stats }
  // Only move a unit into `toKey` when `fromKey` actually had one to give up.
  // In a race where the backend aggregate is refetched (as 0 for this bucket)
  // before this row was counted, an unconditional +1 would inflate the bucket
  // total by one until the next fetchDocuments() heals it.
  const moved = next[fromKey] > 0
  next[fromKey] = Math.max(0, next[fromKey] - 1)
  if (moved) next[toKey] = next[toKey] + 1
  return next
}
